import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useScheduleCall } from "../context/ScheduleCallContext";
const LOGO_SRC = "/photos/purposeLogo.png";

const navItems = [
  { name: "Home", href: "#home", route: "/" },
  { name: "Services", href: "#services", route: "/#services" },
  { name: "Products", href: "#products", route: "/#products" },
  { name: "Work", href: "#portfolio", route: "/#portfolio" },
  { name: "Innovations", href: "#innovations", route: "/#innovations" },
  { name: "About", href: "#about", route: "/#about" },
  { name: "Contact", href: "#contact", route: "/#contact" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openScheduleCall } = useScheduleCall();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const goTo = (item) => {
    if (item.name === "Innovations" && !isHome) {
      navigate("/tools");
      return;
    }
    if (isHome) {
      document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/${item.href}`);
    }
    setIsMobileMenuOpen(false);
  };

  const handleScheduleCall = () => {
    openScheduleCall();
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      aria-label="Main navigation"
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled || !isHome
          ? "bg-dark-400/80 backdrop-blur-xl border-b border-white/5 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="section-container">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_SRC} alt="Purpose Labs logo" className="h-14 w-auto" />
            <span className="font-display text-xl font-bold text-white hidden sm:block">
              Purpose Labs
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.name === "Innovations" ? (
                <Link
                  key={item.name}
                  to="/tools"
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  {item.name}
                </Link>
              ) : (
                <button
                  key={item.name}
                  onClick={() => goTo(item)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  {item.name}
                </button>
              )
            )}
            <motion.button
              onClick={handleScheduleCall}
              className="ml-3 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-500 transition-colors"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Schedule a Call
            </motion.button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" strokeWidth={1.75} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={1.75} />
            )}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden glass-panel mb-4 overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <div className="py-3">
                {navItems.map((item) =>
                  item.name === "Innovations" ? (
                    <Link
                      key={item.name}
                      to="/tools"
                      className="block px-5 py-3 text-gray-400 hover:text-white hover:bg-white/5"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      key={item.name}
                      onClick={() => goTo(item)}
                      className="block w-full text-left px-5 py-3 text-gray-400 hover:text-white hover:bg-white/5"
                    >
                      {item.name}
                    </button>
                  )
                )}
                <div className="px-4 pt-2 pb-3">
                  <button
                    onClick={handleScheduleCall}
                    className="w-full py-3 bg-primary-600 text-white font-semibold rounded-xl"
                  >
                    Schedule a Call
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
